import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useClientList } from '../api/queries.ts';
import { useMyDashboards } from '../api/dashboards.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import type { ClientListGroup, ClientListRow } from '../api/types.ts';
import { HeldMarker, StatusTag, stateTag } from '../components/StatusTag.tsx';
import { useKeepState, usePageSearch } from '../components/pageSearch.ts';

const PAGE_SIZE = 25;

/**
 * The toggle (Arthur, 1 October): who the list shows when nothing is typed.
 * Which status falls in which group is the server's one definition
 * (api/src/legal-status.ts, CLIENT_LIST_GROUPS); this is only the order and the
 * words. Active by default: the list is where somebody goes to find a living
 * client they are responsible for.
 */
const GROUPS: { value: ClientListGroup; label: string }[] = [
  { value: 'Active', label: 'Active' },
  // 106: the guardianship is over and the court has not yet released the agency
  { value: 'Pending discharge', label: 'Pending discharge' },
  { value: 'Discharged', label: 'Discharged' },
  { value: 'Deceased', label: 'Deceased' },
  // 110: never appointed, on a recorded outcome; terminal, and not Discharged
  { value: 'Not appointed', label: 'Not appointed' },
];
const isGroup = (v: string | null): v is ClientListGroup => GROUPS.some((g) => g.value === v);

const SORTS: { value: string; label: string }[] = [
  { value: 'last_name', label: 'Name' },
  { value: 'client_ref', label: 'Reference' },
  { value: 'status', label: 'Status' },
  { value: 'where', label: 'Where they are' },
  { value: 'case_manager', label: 'Case manager' },
];

/**
 * Looking someone up.
 *
 * The most common thing this application will ever do, and the one most likely
 * to be done standing up, one-handed, in a lobby, on somebody else's wifi. So:
 *
 *  - The search box is the first thing, focused, and large enough to type into
 *    without aiming. Everything else on the screen is secondary to it.
 *  - Rows are whole tap targets — a `Link` wrapping the row, not a link on the
 *    name — with a 72px minimum, because the thing being tapped is a person's
 *    record and the cost of hitting the wrong one is opening the wrong client
 *    in front of them.
 *  - The previous results stay on screen while the next query runs. A list
 *    that empties on every keystroke cannot be typed into on a slow
 *    connection, and blanking is the failure this codebase has already made
 *    once, on the client header.
 *  - A Critical flag is marked here, not only on the header. By the time the
 *    header renders, the row has already been chosen; a case manager scanning
 *    for the right person should see which records carry a warning before they
 *    commit to one.
 *
 * Search matches aliases as well as names, which is the whole reason
 * `client_alias` exists — the name in the system is often not the name in the
 * searcher's head. Typing "Fairholm" finds the five clients for whom it is a
 * maiden name and who are recorded under married names.
 */
export function ClientList() {
  // The sort, page and group in the URL; the typed search in the history
  // entry's state, never the URL, because a name in an address is logged
  // (pageSearch.ts). Back and forward bring it back; a reload does not.
  const [params, setParams] = useSearchParams();
  const [q, setSearch] = usePageSearch();
  const keep = useKeepState();
  const sort = params.get('sort') ?? 'last_name';
  const offset = Number(params.get('offset') ?? 0);
  const asked = params.get('group');
  const group: ClientListGroup = isGroup(asked) ? asked : 'Active';

  // Typing updates the box immediately and the query a beat later. 250ms is
  // long enough to not query per keystroke and short enough not to feel like
  // the screen is ignoring you.
  const [typed, setTyped] = useState(q);
  useEffect(() => {
    if (typed === q) return;
    const timer = setTimeout(() => setSearch(typed, ['offset']), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, q]);

  const list = useClientList({ q, sort, offset, limit: PAGE_SIZE, group });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key); else next.set(key, value);
    }
    setParams(next, keep);
  };

  /**
   * What the rows on screen are allowed to claim.
   *
   * Rows render only when they are an answer: this query's, or the last good
   * one while a refresh fails on bad wifi. Two things are not answers:
   *
   *  - Anything after a session failure. A 401, or a 403 from the
   *    authenticator, takes the screen even with rows in hand, because the
   *    server has just said this person may not be shown them.
   *  - Placeholder rows once the request they stand in for has failed. They are
   *    the previous search, kept so typing does not blank the list; but a
   *    failing search for "zzq" otherwise sat under the previous search's 25
   *    rows and "1–25 of 840" for as long as the retries lasted — seen, with
   *    retries paused in a background tab, indefinitely.
   *
   * And the empty state is only for a successful response with zero rows.
   */
  const failure = list.error ?? (list.isPlaceholderData ? list.failureReason : null);
  const takeover =
    failure !== null && (isSessionFailure(failure) || !list.data || list.isPlaceholderData);

  const rows = takeover ? [] : list.data?.clients ?? [];
  const total = takeover ? 0 : list.data?.total ?? 0;
  const showing = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);
  // A typed search spans every group; the server says so by returning no group.
  const searching = q !== '';
  const counts = takeover ? null : list.data?.groups ?? null;

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
      <header className="page-head sticky top-0 z-10 lg:static">
        <h1 className="page-title">Clients</h1>
        {/*
          Stacked on a tablet, one row from lg. The search box is capped rather
          than stretched: a 1500px text input is harder to use than a 400px one,
          and the width is better spent on the rows below.
        */}
        <div className="lg:flex lg:items-center lg:gap-4">

          <label className="sr-only" htmlFor="client-search">
            Search by name, reference or former name
          </label>
          <input
            id="client-search"
            type="search"
            inputMode="search"
            autoComplete="off"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Name, reference, or a former name"
            className="mt-2 min-h-[52px] w-full rounded-lg border border-neutral-300 px-3
                       text-base text-neutral-900 lg:mt-0 lg:min-h-[44px] lg:max-w-[26rem]"
          />

          <div className="mt-2 flex items-center gap-2 lg:mt-0 lg:ml-auto">
            {/* Add a client, Held at load and Discharges to confirm are the Clients
                module's tabs now, and Tasks due is the Tasks module (14 October). */}
            <label className="sr-only" htmlFor="client-sort">Sort by</label>
            <select
              id="client-sort"
              value={sort}
              onChange={(e) => update({ sort: e.target.value, offset: null })}
              className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2
                         text-[0.9375rem] text-neutral-800"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <p className="ml-auto text-sm text-neutral-500 tabular-nums lg:ml-0">
              {takeover ? 'Not loaded'
                : list.isPending || list.isPlaceholderData ? 'Searching…'
                : total === 0 ? (q ? 'No matches' : 'No clients')
                : `${showing}–${to} of ${total}`}
            </p>
          </div>
        </div>

        <GroupToggle
          group={group}
          searching={searching}
          counts={counts}
          onChoose={(g) => update({ group: g === 'Active' ? null : g, offset: null })}
        />
      </header>

      <div className="page-body">
      {/* Column labels exist only where there are columns. */}
      <div className={`hidden border-b border-neutral-200 px-4 py-2 text-xs font-bold
                       tracking-[0.12em] text-neutral-500 uppercase lg:px-5 ${COLUMNS} lg:!grid`}>
        <span>Name</span>
        <span>Ref</span>
        <span>Where they are</span>
        <span>Case manager</span>
        <span className="text-right">Status</span>
      </div>

      {/* One message per cause: the failure panel says it, so Staleness does not. */}
      {!takeover && (
        <Staleness
          updatedAt={list.dataUpdatedAt}
          isFetching={list.isFetching}
          error={list.error}
          onRefresh={() => void list.refetch()}
        />
      )}

      {failure && takeover ? (
        <LoadFailure
          what="The client list"
          error={failure}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : list.isSuccess && !list.isPlaceholderData && rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-neutral-500">
          {q
            ? `Nothing matches “${q}” in any group. Former names are searched too.`
            : group === 'Active' ? 'No clients.' : `No ${group.toLowerCase()} clients.`}
        </p>
      ) : (
        <ol className="divide-y divide-neutral-200">
          {rows.map((client) => <Row key={client.client_id} client={client} marked={searching} />)}
        </ol>
      )}

      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center gap-3 px-4 py-4 lg:max-w-md lg:px-0 lg:pt-4">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => update({ offset: String(Math.max(0, offset - PAGE_SIZE)) })}
            className="min-h-[48px] flex-1 rounded-lg border border-neutral-300 font-semibold
                       text-neutral-700 active:bg-neutral-100 disabled:bg-neutral-100 disabled:text-neutral-600 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={to >= total}
            onClick={() => update({ offset: String(offset + PAGE_SIZE) })}
            className="min-h-[48px] flex-1 rounded-lg border border-neutral-300 font-semibold
                       text-neutral-700 active:bg-neutral-100 disabled:bg-neutral-100 disabled:text-neutral-600 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

/** The column widths, written once so the header row and the data rows cannot
 *  drift apart. Only applies from `lg`; below that the row is a stacked card. */
const COLUMNS =
  'lg:grid lg:grid-cols-[minmax(0,24rem)_7rem_minmax(0,1fr)_minmax(0,13rem)_9rem] lg:items-center lg:gap-4';

function CriticalMark({ count }: { count: number }) {
  // Not a count alone and not colour alone — the same rule as the header, one
  // screen earlier. It says what it is, in words, with a shape.
  return (
    <span className="inline-flex items-center gap-1.5 rounded bg-critical-bg px-2 py-0.5
                     text-xs font-bold tracking-wide text-critical-fg uppercase">
      <svg viewBox="0 0 24 24" aria-hidden="true" className="size-3.5 shrink-0 fill-current">
        <path d="M12 2 1 21h22L12 2Zm0 6 1 7h-2l1-7Zm0 9.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5Z" />
      </svg>
      Critical{count > 1 && ` · ${count}`}
    </span>
  );
}

/**
 * Active / Deceased / Discharged, with the count on each, so the other groups
 * are visibly there rather than discovered.
 *
 * WHILE A SEARCH IS TYPED IT FILTERS NOTHING. The search spans every group,
 * because a filter that hides must never break find: somebody who types a
 * deceased client's name and gets nothing concludes the client is not in the
 * system. So the toggle steps aside, says so, and its counts become the matches
 * in each group; a match outside Active is marked on its row.
 */
function GroupToggle({ group, searching, counts, onChoose }: {
  group: ClientListGroup;
  searching: boolean;
  counts: Record<ClientListGroup, number> | null;
  onChoose: (g: ClientListGroup) => void;
}) {
  const reports = useMyDashboards().data?.dashboards.includes('compliance') ?? false;
  return (
    <div className="mt-3">
      <div role="group" aria-label="Which clients to list" className="flex flex-wrap items-center gap-2">
        {GROUPS.map((g) => {
          const chosen = !searching && g.value === group;
          return (
            <button
              key={g.value}
              type="button"
              aria-pressed={chosen}
              disabled={searching}
              onClick={() => onChoose(g.value)}
              className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3
                          text-[0.9375rem] font-semibold disabled:cursor-default
                          ${chosen
                            ? 'border-neutral-900 bg-neutral-900 text-white'
                            : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 disabled:hover:bg-white'}`}
            >
              {g.label}
              <span className={`tabular-nums ${chosen ? 'text-neutral-200' : 'text-neutral-500'}`}>
                {counts ? counts[g.value] : '…'}
              </span>
            </button>
          );
        })}
        {searching && (
          <p className="text-sm text-neutral-600">
            Searching every client: the counts are the matches in each group.
          </p>
        )}
      </div>
      {!searching && group === 'Deceased' && (
        <p className="mt-2 text-sm text-neutral-600">
          Their statements of death and final reports are in{' '}
          {reports
            ? <Link to="/reports" className="font-semibold text-neutral-900 underline">Reports</Link>
            : 'Reports'}
          , which shows deceased clients by default.
        </p>
      )}
    </div>
  );
}

function Row({ client, marked }: { client: ClientListRow; marked: boolean }) {
  const where = client.whereabouts_unknown
    ? 'Whereabouts unknown'
    : client.where_to_find_them ?? 'Not recorded';

  return (
    <li data-quick-client={client.client_id}>
      <Link
        to={`/clients/${client.client_id}`}
        state={{ from: 'Clients' }}
        // 72px on touch, 52px from lg — denser for scanning with a mouse and
        // still well above the 44px floor, which costs a pointer nothing.
        className={`block min-h-[72px] px-4 py-3 hover:bg-neutral-50 active:bg-neutral-100
                    lg:min-h-[52px] lg:py-2.5 ${COLUMNS}`}
      >
        {/* name — and the ref beside it below lg, where there is no ref column */}
        <div className="flex min-w-0 items-baseline gap-2">
          <p className="min-w-0 truncate font-semibold text-neutral-900">
            {client.display_name}
          </p>
          {/* The client's state, the six-state tag (Active blank). A search
              spans every group, so a match says its state in words; under the
              toggle a tag that only repeats the group heading is left off, and
              one that does not (Pending appointment, inside Active) is shown. */}
          {(marked || stateTag(client.legal_status) !== client.list_group) && (
            <StatusTag status={client.legal_status} />
          )}
          <HeldMarker held={client.held_at_load} />
          <span className="ml-auto shrink-0 font-mono text-sm text-neutral-500 tabular-nums lg:hidden">
            {client.client_ref}
          </span>
        </div>

        <p className="hidden font-mono text-sm text-neutral-500 tabular-nums lg:block">
          {client.client_ref}
        </p>

        <p className="mt-0.5 truncate text-[0.9375rem] text-neutral-600 lg:mt-0">
          <span className={client.whereabouts_unknown ? 'font-semibold text-critical-edge' : undefined}>
            {where}
          </span>
        </p>

        {/* Below lg these two fold into one line; from lg they are columns. */}
        <p className="mt-0.5 truncate text-sm text-neutral-500 lg:mt-0 lg:text-[0.9375rem]">
          <span className="lg:hidden">
            {[client.status, client.case_manager ?? 'No case manager'].filter(Boolean).join(' · ')}
          </span>
          <span className="hidden lg:inline">
            {client.case_manager ?? 'No case manager'}
          </span>
        </p>

        <div className="mt-1 flex items-center gap-2 lg:mt-0 lg:justify-end">
          <span className="hidden truncate text-sm text-neutral-500 lg:inline">
            {client.critical_flags > 0 ? '' : client.status}
          </span>
          {client.critical_flags > 0 && <CriticalMark count={client.critical_flags} />}
        </div>
      </Link>
    </li>
  );
}
